import { useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Target, Flame, Gift, Star, TrendingUp, Medal, Crown } from 'lucide-react';

const Challenges = () => {
  const [currentStreak, setCurrentStreak] = useState(7);
  const [level, setLevel] = useState(12);
  const [experience, setExperience] = useState(75); // percentage to next level

  const [challenges] = useState([
    {
      id: 1,
      title: "No Dining Out Week",
      description: "Skip restaurants for a week and save money by cooking at home",
      reward: 500,
      progress: 5,
      total: 7,
      type: "daily",
      icon: <Flame className="text-orange-500" />
    },
    {
      id: 2,
      title: "Investment Master",
      description: "Invest $100 in your portfolio this month",
      reward: 1000,
      progress: 75,
      total: 100,
      type: "monthly",
      icon: <TrendingUp className="text-blue-500" />
    }
  ]);

  const [goals] = useState([
    {
      id: 1,
      title: "New Car",
      target: 25000,
      current: 15000,
      deadline: "2024-12-31",
      icon: "🚗"
    },
    {
      id: 2,
      title: "Vacation Fund",
      target: 5000,
      current: 3500,
      deadline: "2024-08-01",
      icon: "✈️"
    }
  ]);

  const [achievements] = useState([
    {
      id: 1,
      title: "Savings Superstar",
      description: "Saved $1000 in a month",
      icon: <Star className="text-yellow-500" />,
      earned: true
    },
    {
      id: 2,
      title: "Investment Guru",
      description: "Made first stock investment",
      icon: <Crown className="text-purple-500" />,
      earned: true
    },
    {
      id: 3,
      title: "Budget Master",
      description: "Stayed under budget for 3 months",
      icon: <Medal className="text-blue-500" />,
      earned: false
    }
  ]);

  return (
    <div className="space-y-6">
      {/* User Progress */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-md rounded-2xl p-6 shadow-xl"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center gap-4 p-4 bg-[#DCE9C2] rounded-xl">
            <Flame className="w-12 h-12 text-[#68A511]" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Current Streak</p>
              <p className="text-2xl font-bold text-[#598615]">{currentStreak} days</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4 p-4 bg-[#DCE9C2] rounded-xl">
            <Trophy className="w-12 h-12 text-[#68A511]" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Level</p>
              <p className="text-2xl font-bold text-[#598615]">{level}</p>
            </div>
          </div>

          <div className="p-4 bg-[#DCE9C2] rounded-xl">
            <div className="flex justify-between mb-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">Experience</p>
              <p className="text-sm font-medium text-[#598615]">{experience}%</p>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-[#68A511] h-2.5 rounded-full"
                style={{ width: `${experience}%` }}
              />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Active Challenges */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-md rounded-2xl p-6 shadow-xl"
      >
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-6">
          Active Challenges
        </h2>
        <div className="space-y-4">
          {challenges.map((challenge) => (
            <div
              key={challenge.id}
              className="p-4 border border-[#DCE9C2] rounded-xl hover:bg-[#DCE9C2]/10 transition-colors"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {challenge.icon}
                  <div>
                    <h3 className="font-medium text-gray-700 dark:text-gray-200">
                      {challenge.title}
                    </h3>
                    <p className="text-sm text-gray-500">{challenge.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Gift size={16} className="text-[#68A511]" />
                  <span className="text-[#68A511] font-medium">{challenge.reward} pts</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Progress</span>
                  <span className="text-[#598615] font-medium">
                    {challenge.progress}/{challenge.total} {challenge.type === 'daily' ? 'days' : 'dollars'}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-[#68A511] h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(challenge.progress / challenge.total) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Financial Goals */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-md rounded-2xl p-6 shadow-xl"
      >
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-6">
          Financial Goals
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {goals.map((goal) => (
            <div
              key={goal.id}
              className="p-4 border border-[#DCE9C2] rounded-xl hover:bg-[#DCE9C2]/10 transition-colors"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="text-4xl">{goal.icon}</span>
                <div>
                  <h3 className="font-medium text-gray-700 dark:text-gray-200">
                    {goal.title}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Due by {new Date(goal.deadline).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Progress</span>
                  <span className="text-[#598615] font-medium">
                    ${goal.current.toLocaleString()} / ${goal.target.toLocaleString()}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-[#68A511] h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(goal.current / goal.target) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Achievements */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-md rounded-2xl p-6 shadow-xl"
      >
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-6">
          Achievements
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {achievements.map((achievement) => (
            <div
              key={achievement.id}
              className={`p-4 rounded-xl text-center ${
                achievement.earned 
                  ? 'bg-[#DCE9C2]' 
                  : 'bg-gray-100 dark:bg-gray-700/50 opacity-50'
              }`}
            >
              <div className="flex justify-center mb-3">
                {achievement.icon}
              </div>
              <h3 className="font-medium text-gray-700 dark:text-gray-200">
                {achievement.title}
              </h3>
              <p className="text-sm text-gray-500">{achievement.description}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default Challenges; 